import './dropdown.scss';

import { type ReactNode, type RefObject, useEffect, useRef } from 'react';

import { cn } from '@/shared/lib/utils';

export interface DropdownItem {
  id: string;
  icon: ReactNode;
  label: string;
  sublabel?: ReactNode;
  selected?: boolean;
  onClick?: () => void;
}

export interface DropdownAction {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
}

interface DropdownProps {
  open?: boolean;
  items?: DropdownItem[];
  actions?: DropdownAction[];
  className?: string;
  onClose?: () => void;
  triggerRef?: RefObject<HTMLElement | null>;
}

export function Dropdown({
  open = true,
  items = [],
  actions = [],
  className,
  onClose,
  triggerRef,
}: DropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onClose) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (triggerRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, triggerRef]);

  return (
    <div ref={ref} className={cn('dropdown', open && 'dropdown--open', className)}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={cn('dropdown__row', item.selected && 'dropdown__row--selected')}
          onClick={item.onClick}>
          <span className="dropdown__icon">{item.icon}</span>
          <span className="dropdown__content">
            <span className="dropdown__label">{item.label}</span>
            {item.sublabel && <span className="dropdown__sublabel">{item.sublabel}</span>}
          </span>
          {item.selected && <span className="dropdown__check">✓</span>}
        </button>
      ))}

      {items.length > 0 && actions.length > 0 && <div className="dropdown__separator" />}

      {actions.map((action, i) => (
        <button key={i} type="button" className="dropdown__action" onClick={action.onClick}>
          <span className="dropdown__icon dropdown__icon--muted">{action.icon}</span>
          <span className="dropdown__action-label">{action.label}</span>
        </button>
      ))}
    </div>
  );
}
